import { useState, type FC } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "./markdown-components";
import type { RenderBlock, AppWidgetBlock } from "./types";
import { AppWidget } from "./AppWidget";
import {
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
} from "./AppWidgetVariants";

type BlockRendererProps = {
  block: RenderBlock;
};

export const BlockRenderer: FC<BlockRendererProps> = ({ block }) => {
  if (block.type === "text") {
    return (
      <div className="aui-md">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {block.markdown}
        </ReactMarkdown>
      </div>
    );
  }

  if (block.type === "code") {
    return <CodeBlock language={block.language} code={block.code} />;
  }

  if (block.type === "app_widget") {
    return <AppWidgetBlockRenderer block={block} />;
  }

  // Exhaustive check
  const _exhaustive: never = block;
  return null;
};

/**
 * Renders an app widget block within a conversation
 */
const AppWidgetBlockRenderer: FC<{ block: AppWidgetBlock }> = ({ block }) => {
  const { appName, appIcon, variant } = block;

  if (variant === "list" && block.items) {
    return (
      <AppWidgetList appName={appName} appIcon={appIcon} items={block.items} />
    );
  }

  if (variant === "image") {
    return (
      <AppWidgetImage
        appName={appName}
        appIcon={appIcon}
        images={block.images}
        fallback={block.fallback}
      />
    );
  }

  if (variant === "track_list" && block.tracks) {
    return (
      <AppWidgetTrackList
        appName={appName}
        appIcon={appIcon}
        items={block.tracks}
        footerCta={block.footerCta}
        footerText={block.footerText}
      />
    );
  }

  if (variant === "cart" && block.cartItems) {
    return (
      <AppWidgetCart
        appName={appName}
        appIcon={appIcon}
        items={block.cartItems}
        subtotal={block.subtotal || "$0.00"}
        deliveryTime={block.deliveryTime}
        primaryCta={block.primaryCta}
      />
    );
  }

  if (variant === "confirmation" && block.confirmationTitle) {
    return (
      <AppWidgetConfirmation
        appName={appName}
        appIcon={appIcon}
        title={block.confirmationTitle}
        subtitle={block.confirmationSubtitle}
        image={block.confirmationImage}
        details={block.confirmationDetails}
        actions={block.confirmationActions}
      />
    );
  }

  // Custom/fallback - render generic widget with content
  return (
    <AppWidget appName={appName} appIcon={appIcon}>
      {block.content ? (
        <div
          className="app-widget__custom-content"
          dangerouslySetInnerHTML={{ __html: block.content }}
        />
      ) : (
        <div className="app-widget__empty">No content</div>
      )}
    </AppWidget>
  );
};

type CodeBlockProps = {
  language: string;
  code: string;
};

const CopyIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CodeBlock: FC<CodeBlockProps> = ({ language, code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-block-language">{language}</span>
        <button className="code-block-copy" onClick={handleCopy}>
          {copied ? (
            <>
              <CheckIcon />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <CopyIcon />
              <span>Copy code</span>
            </>
          )}
        </button>
      </div>
      <pre className="code-block-content">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  );
};
